/**
 * The consent card: a real question with two equal answers.
 *
 * Product analytics (PostHog, see lib/analytics/posthog.ts) starts only after
 * Allow, and this card is the only place that answer is given. Allow and
 * Don't allow are the same size and the same style, because a choice where
 * one answer is easier to press than the other is not much of a choice.
 *
 * IT NEVER ASKS A QUESTION WHOSE ANSWER WOULD BE IGNORED: not when the browser
 * sends Global Privacy Control (that is a No already), not for an account that
 * may belong to a minor, not while an admin is viewing as somebody else, and
 * not on a page reached through a link carrying a secret. shouldAskConsent in
 * lib/analytics/consent.ts holds that rule.
 *
 * NOTHING IS SENT ABOUT THE ANSWER. Pressing either button fires no event:
 * before Allow there is nothing running to send one, and recording a No would
 * be collecting from somebody who said no.
 *
 * Mounted at the end of every layout, so it is last in the tab order as well
 * as last on the screen. "Privacy choices" in the footer and on the Account
 * page clears the answer, which brings the card back.
 */

import { Link } from 'react-router';

import { Button } from '../primitives/Button';
import { t } from '../../lib/i18n/t';
import { shouldAskConsent, useConsentStore } from '../../lib/analytics/consent';
import { useAnalyticsGate } from '../../lib/analytics/PostHogProvider';

import '../../styles/components/consent-notice.css';

export function ConsentNotice() {
  const gate = useAnalyticsGate();
  const setChoice = useConsentStore((s) => s.set);

  if (!shouldAskConsent(gate)) return null;

  return (
    <aside className="consent" aria-label={t('Analytics choice')}>
      <p className="consent__say">
        {t(
          'May we measure how this site is used? With your OK we count page visits and clicks using PostHog, with no screen recording and no email address. Nothing is collected unless you allow it.',
        )}
      </p>
      <div className="consent__actions">
        <Button variant="outline" size="sm" onClick={() => setChoice('granted')}>
          {t('Allow')}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setChoice('denied')}>
          {t("Don't allow")}
        </Button>
        <Link className="link-inline consent__more" to="/privacy">
          {t('Privacy policy')}
        </Link>
      </div>
    </aside>
  );
}

/** Clears the stored answer so the card asks again. For footer and Account links. */
export function PrivacyChoicesLink({ className }: { className?: string }) {
  const reset = useConsentStore((s) => s.reset);
  return (
    <button type="button" className={className ?? 'link-inline'} onClick={reset}>
      {t('Privacy choices')}
    </button>
  );
}
